import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, authClient, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import SectionHeader from "@/components/SectionHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { sfx } from "@/lib/sounds";
import { Upload, X, ImageIcon } from "lucide-react";

const CATEGORIES = ["Spawn", "Medieval", "Mega Base", "Arena", "Cyber", "Village", "Redstone", "Memory", "Other"];

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function downscale(dataUrl, maxWidth = 1400) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width <= maxWidth) return resolve(dataUrl);
      const ratio = maxWidth / width;
      const canvas = document.createElement("canvas");
      canvas.width = maxWidth;
      canvas.height = Math.round(height * ratio);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

export default function BuildUpload() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [form, setForm] = useState({
    title: "", description: "", category: "Spawn", coordinates: "",
    team: "", tags: "", server: "LostDC Survival S8", world_download_url: "",
  });
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);

  if (!loading && !user) {
    navigate("/");
    return null;
  }

  const addFiles = async (files) => {
    const arr = Array.from(files).slice(0, 6 - images.length);
    for (const f of arr) {
      if (!f.type.startsWith("image/")) continue;
      const raw = await fileToDataUrl(f);
      const smaller = await downscale(raw, 1400);
      setImages((prev) => [...prev, smaller]);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title required");
    if (images.length === 0) return toast.error("Add at least one screenshot");
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        coordinates: form.coordinates.trim(),
        team: form.team.split(",").map((s) => s.trim()).filter(Boolean),
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        server: form.server,
        world_download_url: form.world_download_url.trim(),
        images,
      };
      await authClient.post("/builds", payload);
      sfx.levelup();
      toast.success("Build submitted!", { description: "Pending admin review." });
      navigate("/profile");
    } catch (err) {
      sfx.error();
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  return (
    <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="page-build-upload">
      <div className="max-w-3xl mx-auto">
        <SectionHeader eyebrow="Community Builds" title="Upload Your Build" subtitle="Screenshots, coordinates, teammates. Your build goes live once approved." />

        <form onSubmit={submit} className="rounded-2xl glass p-6 space-y-4">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">Title *</span>
            <Input data-testid="build-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-black/60 border-white/10 text-white" />
          </label>

          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">Category</span>
              <select data-testid="build-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-black/60 border border-white/10 rounded-md px-3 py-2 text-white">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">Server</span>
              <Input value={form.server} onChange={(e) => setForm({ ...form, server: e.target.value })} className="bg-black/60 border-white/10 text-white" />
            </label>
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">Description</span>
            <Textarea data-testid="build-description" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-black/60 border-white/10 text-white" />
          </label>

          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">Coordinates</span>
              <Input placeholder="e.g. -123, 65, 890" value={form.coordinates} onChange={(e) => setForm({ ...form, coordinates: e.target.value })} className="bg-black/60 border-white/10 text-white" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">World Download URL</span>
              <Input value={form.world_download_url} onChange={(e) => setForm({ ...form, world_download_url: e.target.value })} className="bg-black/60 border-white/10 text-white" />
            </label>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">Team (comma separated)</span>
              <Input placeholder="AarushCraft, DevPvP" value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })} className="bg-black/60 border-white/10 text-white" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">Tags (comma separated)</span>
              <Input placeholder="epic, redstone" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="bg-black/60 border-white/10 text-white" />
            </label>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Screenshots (up to 6)</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {images.map((src, i) => (
                <div key={i} className="relative aspect-video rounded-lg overflow-hidden border border-white/10">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setImages(images.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 p-1 rounded-full bg-black/70 hover:bg-red-500/70">
                    <X size={12} />
                  </button>
                </div>
              ))}
              {images.length < 6 && (
                <button
                  type="button"
                  data-testid="build-add-image"
                  onClick={() => inputRef.current?.click()}
                  className="aspect-video rounded-lg border-2 border-dashed border-white/15 hover:border-[#FFBF00]/50 grid place-items-center text-white/50 hover:text-[#FFBF00]"
                >
                  <div className="flex flex-col items-center gap-1">
                    <ImageIcon size={20} /> <span className="text-[10px] uppercase tracking-widest">Add</span>
                  </div>
                </button>
              )}
            </div>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={(e) => addFiles(e.target.files || [])}
            />
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="build-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#FFBF00] text-black font-semibold uppercase tracking-wider hover:bg-[#FF8C00] transition-colors disabled:opacity-60"
          >
            <Upload size={16} /> {busy ? "Uploading..." : "Submit for Approval"}
          </button>
        </form>
      </div>
    </div>
  );
}
